import { ChevronRight, MapPin, Users } from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { cn, formatCurrency, formatDateDDMMYYYY } from "../../../lib/utils";
import { RecentDriver } from "../@types";
import { DashCard, EmptyState } from "./DashCard";
import DriverAvatar from "./DriverAvatar";

const DAY_MS = 24 * 60 * 60 * 1000;

const licenseStatus = (expiry?: string | null) => {
  if (!expiry) return null;
  const date = new Date(expiry);
  if (isNaN(date.getTime())) return null;
  const daysLeft = Math.ceil((date.getTime() - Date.now()) / DAY_MS);
  if (daysLeft < 0)
    return { label: "Expired", className: "bg-red-50 text-red-700" };
  if (daysLeft <= 30)
    return {
      label: `${daysLeft}d left`,
      className: "bg-amber-50 text-amber-700",
    };
  return null;
};

const performanceStyle = (value?: string) => {
  if (!value) return "bg-slate-100 text-slate-600";
  if (/excellent|good|profit|above|high|positive/i.test(value))
    return "bg-emerald-50 text-emerald-700";
  if (/poor|bad|loss|below|low|negative/i.test(value))
    return "bg-red-50 text-red-700";
  return "bg-amber-50 text-amber-700";
};

const tripStatusStyle = (status?: string) =>
  /ongoing|progress|active|transit/i.test(status ?? "")
    ? "bg-emerald-50 text-emerald-700"
    : /cancel/i.test(status ?? "")
    ? "bg-red-50 text-red-700"
    : "bg-slate-100 text-slate-600";

const Dash = () => <span className="text-sm text-slate-400">—</span>;

interface RecentDriversTableProps {
  drivers: RecentDriver[];
}

const RecentDriversTable: React.FC<RecentDriversTableProps> = ({ drivers }) => {
  const navigate = useNavigate();

  return (
    <DashCard
      title="Recent driver activity"
      subtitle="Assignment performance for your most active drivers"
      bodyClassName="px-0 pb-2 pt-4"
      action={
        <button
          type="button"
          onClick={() => navigate("/admin/drivers")}
          className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline"
        >
          All drivers <ChevronRight size={14} />
        </button>
      }
    >
      {drivers.length === 0 ? (
        <EmptyState
          icon={<Users size={20} />}
          message="No recent driver activity"
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-left">
            <thead>
              <tr className="border-y border-slate-100 bg-slate-50/70 text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3">Driver</th>
                <th className="px-5 py-3">Vehicle</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Income vs target</th>
                <th className="px-5 py-3">Performance</th>
                <th className="px-5 py-3">Last trip</th>
                <th className="px-5 py-3">License</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {drivers.map((driver) => {
                const license = licenseStatus(driver.licenseExpiryDate);
                const target = driver.incomeTarget ?? 0;
                const income = driver.currentAssignmentIncome ?? 0;
                const progress =
                  target > 0 ? Math.min(100, (income / target) * 100) : 0;

                return (
                  <tr
                    key={driver.id}
                    onClick={() => navigate(`/admin/drivers/${driver.id}`)}
                    className="cursor-pointer transition-colors hover:bg-slate-50/70"
                  >
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <DriverAvatar
                            name={driver.fullName}
                            imageUrl={driver.imageUrl}
                          />
                          <span
                            className={cn(
                              "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white",
                              driver.isActive ? "bg-emerald-500" : "bg-slate-300"
                            )}
                            title={driver.isActive ? "Active" : "Inactive"}
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-900">
                            {driver.fullName}
                          </p>
                          <p className="truncate text-xs text-slate-500">
                            {driver.phoneNumber || driver.email}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-5 py-3.5">
                      {driver.currentVehicle ? (
                        <>
                          <p className="text-sm text-slate-800">
                            {driver.currentVehicle}
                          </p>
                          {driver.currentVehicleType && (
                            <p className="text-xs text-slate-500">
                              {driver.currentVehicleType}
                            </p>
                          )}
                        </>
                      ) : (
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-500">
                          Unassigned
                        </span>
                      )}
                    </td>

                    <td className="px-5 py-3.5">
                      {driver.tripStatus ? (
                        <span
                          className={cn(
                            "rounded-full px-2.5 py-1 text-xs font-medium",
                            tripStatusStyle(driver.tripStatus)
                          )}
                        >
                          {driver.tripStatus}
                        </span>
                      ) : (
                        <Dash />
                      )}
                      {driver.currentLocation && (
                        <p className="mt-1.5 flex max-w-[160px] items-center gap-1 truncate text-xs text-slate-500">
                          <MapPin size={11} className="shrink-0" />
                          <span className="truncate">
                            {driver.currentLocation}
                          </span>
                        </p>
                      )}
                    </td>

                    <td className="px-5 py-3.5">
                      <p className="text-sm font-medium text-slate-900">
                        {formatCurrency(income)}
                      </p>
                      {target > 0 ? (
                        <div className="mt-1.5 flex items-center gap-2">
                          <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100">
                            <div
                              className={cn(
                                "h-full rounded-full",
                                progress >= 100
                                  ? "bg-emerald-500"
                                  : progress >= 50
                                  ? "bg-primary"
                                  : "bg-amber-400"
                              )}
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500">
                            {Math.round(progress)}%
                          </span>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">No target set</p>
                      )}
                    </td>

                    <td className="px-5 py-3.5">
                      {driver.currentAssignmentFinancialPerformance ? (
                        <span
                          className={cn(
                            "rounded-full px-2.5 py-1 text-xs font-medium",
                            performanceStyle(
                              driver.currentAssignmentFinancialPerformance
                            )
                          )}
                        >
                          {driver.currentAssignmentFinancialPerformance}
                        </span>
                      ) : (
                        <Dash />
                      )}
                      <p className="mt-1.5 text-xs text-slate-500">
                        Yield {formatCurrency(driver.currentAssignmentYield)}
                      </p>
                    </td>

                    <td className="px-5 py-3.5">
                      {driver.lastTripDate ? (
                        <>
                          <p className="text-sm text-slate-800">
                            {formatDateDDMMYYYY(driver.lastTripDate)}
                          </p>
                          <p className="text-xs text-slate-500">
                            {(driver.lastTripDistance ?? 0).toLocaleString()} km
                          </p>
                        </>
                      ) : (
                        <Dash />
                      )}
                    </td>

                    <td className="px-5 py-3.5">
                      {license ? (
                        <span
                          className={cn(
                            "rounded-full px-2.5 py-1 text-xs font-medium",
                            license.className
                          )}
                        >
                          {license.label}
                        </span>
                      ) : driver.licenseExpiryDate ? (
                        <span className="text-sm text-slate-600">
                          {formatDateDDMMYYYY(driver.licenseExpiryDate)}
                        </span>
                      ) : (
                        <Dash />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DashCard>
  );
};

export default RecentDriversTable;
